/**
 * The uploaded prescription, for reading while it is transcribed (Rx-12). A photo can be zoomed
 * (the buttons, or Ctrl/⌘ + wheel), fitted to the width or the whole page, turned a quarter at a
 * time (phone photos often arrive sideways) and dragged to move around it; a PDF shows in the
 * browser's own viewer, or on a phone hands over to the device's (PdfView, RESP-08). Either opens
 * full-size in a new tab or downloads. Anything else can only
 * be downloaded (ORD-02). A photo's turn is remembered for the upload in this browser, so coming
 * back to it shows it the right way up.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import { fetchUploadImage } from "../../api/pharmacist";
import { describeError } from "../../api/errors";
import { PdfView } from "../../components/ui/PdfView";

const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3, 4];
const PAD = 16; // the frame's padding around the picture, px

type Loaded = { url: string; contentType: string; filename: string };

export function DocumentViewer({ uploadId, className = "" }: { uploadId: string; className?: string }) {
  const [file, setFile] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    setFile(null);
    setError(null);
    fetchUploadImage(uploadId)
      .then((res) => {
        if (cancelled) {
          URL.revokeObjectURL(res.url);
          return;
        }
        url = res.url;
        setFile(res);
      })
      .catch((e) => !cancelled && setError(describeError(e)));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [uploadId]);

  const isImage = !!file && file.contentType.startsWith("image/");
  const isPdf = !!file && file.contentType.includes("pdf");

  let body: ReactNode;
  if (error) body = <Centered tone="text-red-600">{error}</Centered>;
  else if (!file) body = <Centered>Loading the prescription…</Centered>;
  else if (isImage) body = <ImageView src={file.url} uploadId={uploadId} />;
  else if (isPdf) body = <PdfView url={file.url} filename={file.filename || "prescription.pdf"} className="h-full w-full" />;
  else
    body = (
      <Centered>
        This file can't be shown here.{" "}
        <a href={file.url} download={file.filename || "prescription"} className="font-medium text-brand underline">
          Download it
        </a>
      </Centered>
    );

  return (
    <section aria-label="Uploaded prescription" className={`flex flex-col overflow-hidden rounded-xl border bg-white shadow-xs ${className}`}>
      {/* The image's own controls sit in ImageView; these work for any file. */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2 text-sm">
        <span className="font-medium text-gray-700">Prescription</span>
        {file && (
          <div className="flex items-center gap-1">
            <a href={file.url} target="_blank" rel="noreferrer" className="rounded-md px-2 py-1 text-brand hover:bg-brand-50">
              Open ↗
            </a>
            <a href={file.url} download={file.filename || "prescription"} className="rounded-md px-2 py-1 text-brand hover:bg-brand-50">
              Download
            </a>
          </div>
        )}
      </div>
      <div className="relative min-h-0 flex-1 bg-gray-100">{body}</div>
    </section>
  );
}

function Centered({ children, tone = "text-gray-400" }: { children: ReactNode; tone?: string }) {
  return <div className={`flex h-full items-center justify-center p-6 text-center text-sm ${tone}`}>{children}</div>;
}

const turnKey = (uploadId: string) => `dp.upload-turn.${uploadId}`;

function readTurn(uploadId: string): number {
  try {
    const t = Number(window.localStorage.getItem(turnKey(uploadId)));
    return [90, 180, 270].includes(t) ? t : 0;
  } catch {
    return 0;
  }
}

function keepTurn(uploadId: string, turn: number): void {
  try {
    if (turn) window.localStorage.setItem(turnKey(uploadId), String(turn));
    else window.localStorage.removeItem(turnKey(uploadId));
  } catch {
    /* storage blocked: the turn just isn't remembered */
  }
}

function ImageView({ src, uploadId }: { src: string; uploadId: string }) {
  const viewport = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  // `zoom` is a multiple of "fits the width"; `rotation` a quarter-turn count in degrees.
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(() => readTurn(uploadId));
  const turn = (by: number) => {
    const next = (rotation + by + 360) % 360;
    keepTurn(uploadId, next);
    setRotation(next);
  };

  useLayoutEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const measure = () => setBox({ w: Math.max(0, el.clientWidth - PAD * 2), h: Math.max(0, el.clientHeight - PAD * 2) });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Ctrl/⌘ + wheel zooms (a plain wheel scrolls). React's wheel listener is passive, so it
  // couldn't stop the browser zooming the whole page instead.
  useEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZoom((z) => step(z, e.deltaY < 0 ? 1 : -1));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Drag with the mouse to move around a zoomed picture (touch scrolls natively).
  const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  const sideways = rotation % 180 !== 0;
  const ratio = natural ? natural.h / natural.w : 4 / 3;
  // The picture as turned: its width is the box's times the zoom.
  const shownW = box.w * zoom;
  const imgW = sideways ? shownW / ratio : shownW;
  const imgH = imgW * ratio;
  const frameW = sideways ? imgH : imgW;
  const frameH = sideways ? imgW : imgH;
  // The zoom at which the whole turned picture fits the box.
  const fitPage = box.w && box.h ? Math.min(1, box.h / (sideways ? box.w / ratio : box.w * ratio)) : 1;

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-1 border-b bg-white px-2 py-1.5 text-sm" role="toolbar" aria-label="Picture controls">
        <ToolButton label="Zoom out" onClick={() => setZoom((z) => step(z, -1))} disabled={zoom <= ZOOM_STEPS[0]}>
          −
        </ToolButton>
        <span className="w-12 text-center text-xs tabular-nums text-gray-500" aria-live="polite">
          {Math.round(zoom * 100)}%
        </span>
        <ToolButton label="Zoom in" onClick={() => setZoom((z) => step(z, 1))} disabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}>
          +
        </ToolButton>
        <span className="mx-1 h-5 w-px bg-gray-200" />
        <ToolButton label="Fit the width" onClick={() => setZoom(1)} active={zoom === 1}>
          Fit width
        </ToolButton>
        <ToolButton label="Show the whole page" onClick={() => setZoom(fitPage)} active={Math.abs(zoom - fitPage) < 0.001 && zoom !== 1}>
          Whole page
        </ToolButton>
        <span className="mx-1 h-5 w-px bg-gray-200" />
        <ToolButton label="Turn left" onClick={() => turn(-90)}>
          ⟲
        </ToolButton>
        <ToolButton label="Turn right" onClick={() => turn(90)}>
          ⟳
        </ToolButton>
      </div>

      <div
        ref={viewport}
        className={`relative min-h-0 flex-1 overflow-auto ${dragging ? "cursor-grabbing select-none" : "cursor-grab"}`}
        style={{ padding: PAD }}
        onPointerDown={(e) => {
          if (e.pointerType !== "mouse" || e.button !== 0 || !viewport.current) return;
          drag.current = { x: e.clientX, y: e.clientY, left: viewport.current.scrollLeft, top: viewport.current.scrollTop };
          viewport.current.setPointerCapture(e.pointerId);
          setDragging(true);
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d || !viewport.current) return;
          viewport.current.scrollLeft = d.left - (e.clientX - d.x);
          viewport.current.scrollTop = d.top - (e.clientY - d.y);
        }}
        onPointerUp={() => {
          drag.current = null;
          setDragging(false);
        }}
        onPointerCancel={() => {
          drag.current = null;
          setDragging(false);
        }}
      >
        {box.w > 0 && (
          <div className="relative mx-auto" style={{ width: frameW, height: frameH }}>
            <img
              src={src}
              alt="Uploaded prescription"
              draggable={false}
              onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth || 1, h: e.currentTarget.naturalHeight || 1 })}
              className="absolute max-w-none rounded-sm bg-white shadow-sm"
              style={{
                width: imgW,
                height: imgH,
                left: (frameW - imgW) / 2,
                top: (frameH - imgH) / 2,
                transform: rotation ? `rotate(${rotation}deg)` : undefined,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** The next zoom step in or out from `z` (from a fitted zoom, the nearest step that way). */
function step(z: number, dir: 1 | -1): number {
  if (dir > 0) return ZOOM_STEPS.find((s) => s > z + 0.001) ?? ZOOM_STEPS[ZOOM_STEPS.length - 1];
  return [...ZOOM_STEPS].reverse().find((s) => s < z - 0.001) ?? ZOOM_STEPS[0];
}

function ToolButton({
  label,
  onClick,
  disabled = false,
  active = false,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      aria-pressed={active || undefined}
      className={`min-w-8 rounded-md px-2 py-1 text-sm transition disabled:opacity-40 ${
        active ? "bg-brand-50 font-medium text-brand" : "text-gray-700 hover:bg-gray-100"
      }`}
    >
      {children}
    </button>
  );
}
